import { createFeatureSelector, createSelector } from '@ngrx/store';
import { IPnpBudgetState } from '../reducers/pnp-budget.reducer';
import {Order} from '../../models/order.model';

export const selectPnpBudgetState =
  createFeatureSelector<IPnpBudgetState>('pnpBudget');

export const selectUserBudgets = createSelector(
  selectPnpBudgetState,
  (state: IPnpBudgetState) => state.userBudgets
);

export const selectIsFetchingUserBudgets = createSelector(
  selectPnpBudgetState,
  (state: IPnpBudgetState) => state.fetchingUserBudgets
);

export const selectHasUserBudgets = createSelector(
  selectUserBudgets,
  (budgets) => budgets.length > 0
);

export const selectFetchingForOrderId = createSelector(
  selectPnpBudgetState,
  (state: IPnpBudgetState) => state.fetchingForOrderId
);

export const selectPnpBudgetsByOrderId = (orderId: number) =>
  createSelector(
    selectPnpBudgetState,
    (state: IPnpBudgetState) => state.budgetsByOrderId[orderId]?.budgets || []
  );

export const selectOrderBudgetsFetched = (orderId: number) =>
  createSelector(
    selectPnpBudgetState,
    (state: IPnpBudgetState) => state.budgetsByOrderId[orderId]?.fetched ?? false
  );

export const selectBudgetAvailableForOrder = (orderId: number) =>
  createSelector(
    selectPnpBudgetState,
    (state: IPnpBudgetState) => state.budgetAvailableByOrderId[orderId]?.available ?? false
  );

export const selectBudgetAvailableFetched = (orderId: number) =>
  createSelector(
    selectPnpBudgetState,
    (state: IPnpBudgetState) => state.budgetAvailableByOrderId[orderId]?.fetched ?? false
  );

export const selectIsFetchingBudgetAvailableForOrder = (orderId: number) =>
  createSelector(
    selectPnpBudgetState,
    (state: IPnpBudgetState) => state.budgetAvailableByOrderId[orderId]?.fetching ?? false
  );

export const selectHasBudgetsForOrderId = (orderId: number) =>
  createSelector(
    selectPnpBudgetsByOrderId(orderId),
    (budgets) => budgets.length > 0
  );

export const selectTotalBudgetUsedForOrderId = (orderId: number) =>
  createSelector(
    selectPnpBudgetsByOrderId(orderId),
    (budgets) =>
      budgets.reduce((sum, budget) => {
        const remaining = budget.budget_remaining || 0;
        const budgetTotal = Math.max(0, (budget.budget_used || 0) + (remaining < 0 ? remaining : 0));
        return sum + budgetTotal;
      }, 0)
  );

export const selectBudgetCurrencyForOrderId = (orderId: number) =>
  createSelector(
    selectPnpBudgetsByOrderId(orderId),
    (budgets) => budgets.length > 0 ? budgets[0].currency : null
  );

export const selectIsBudgetOverdraft = (orderId: number) =>
  createSelector(
    selectPnpBudgetsByOrderId(orderId),
    (budgets) => budgets.some(budget => budget.budget_remaining < 0)
  );


// user can apply  for budget  if order id > 0, order.item_count > 0 ,  order is in editable state (quote)
// and user has budgets  and budget for order is not fetched yet
export const selectCanApplyBudgetToOrder = (order: Order) =>
  createSelector(
    selectHasUserBudgets,
    selectOrderBudgetsFetched(order?.id),
    selectBudgetAvailableForOrder(order?.id),
    (hasUserBudgets, budgetsFetched, budgetAvailable) => {
      if (!order || order.id <= 0 || order.item_count <= 0 || !order.isEditable) {
        return false;
      }
      return hasUserBudgets && !budgetsFetched && budgetAvailable;
    }
  );


